import { useState } from "react";
import { useNavigate } from "react-router-dom";
import authApi from "../api/authApi";
import { tokenStore } from "../api/axiosClient";

const emptyForm = {
  email: "",
  password: "",
  rememberMe: false,
  totpCode: "",
};

export default function LoginPage() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [needCode, setNeedCode] = useState(false);
  const navigate = useNavigate();

  const handleChange = (field) => (e) => {
    const value = field === "rememberMe" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Map backend errorCode → friendlier UI message. Falls back to the server's
  // message if we don't have a specific override.
  const friendlyError = (err) => {
    const data = err?.response?.data;
    const code = data?.errorCode;
    const serverMessage = data?.message || err?.message;

    switch (code) {
      case "INVALID_CREDENTIALS":
        return "Email or password is incorrect.";
      case "ACCOUNT_LOCKED":
        return "Too many failed attempts. Please try again in 15 minutes.";
      case "ACCOUNT_SUSPENDED":
        return "Your account has been suspended. Contact your administrator.";
      case "TOTP_REQUIRED":
        return "Enter the 6-digit code from your authenticator app (or a recovery code).";
      case "TOTP_INVALID":
        return "That code is not correct. Try the current code from your app, or a recovery code.";
      case "ACCOUNT_INACTIVE":
        return "Your account is inactive. Contact your administrator.";
      default:
        return serverMessage || "Unable to sign in. Please try again.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    setError("");
    setNotice("");
    setLoading(true);

    try {
      const { data } = await authApi.login({
        email: form.email,
        password: form.password,
        rememberMe: form.rememberMe,
        totpCode: form.totpCode.trim() || undefined,
      });

      tokenStore.setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        tokenType: data.tokenType || "Bearer",
        user: data.user,
        rememberMe: form.rememberMe,
      });

      setNotice("Signed in. Redirecting to dashboard...");
      setTimeout(() => navigate("/", { replace: true }), 300);
    } catch (err) {
      const c = err?.response?.data?.errorCode;
      if (c === "TOTP_REQUIRED" || c === "TOTP_INVALID") setNeedCode(true);
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-intro">
          <div className="pill">AdSense Protector · Admin</div>
          <h1>Welcome back, guardian.</h1>
          <p className="lede">
            Securely access traffic KPIs and live session intelligence with your admin credentials.
          </p>
          <div className="bullets">
            <div><span className="dot" /> Token-based security with refresh support.</div>
            <div><span className="dot" /> Unified view of sessions, metrics, and incidents.</div>
            <div><span className="dot" /> Optimized for fast handoffs between teams.</div>
          </div>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <p className="eyebrow">Admin Console Login</p>
          <h3>Sign in to continue</h3>

          {error && <div className="toast error">{error}</div>}
          {notice && <div className="toast">{notice}</div>}

          <label className="field">
            Email
            <input
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              autoComplete="email"
              required
              placeholder="name@company.com"
              disabled={loading}
            />
          </label>

          <label className="field">
            Password
            <input
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              autoComplete="current-password"
              required
              placeholder="••••••••"
              disabled={loading}
            />
          </label>

          {needCode && (
            <label className="field">
              Authenticator code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={form.totpCode}
                onChange={handleChange("totpCode")}
                placeholder="123456"
                autoFocus
                disabled={loading}
              />
            </label>
          )}

          <label className="remember">
            <input
              type="checkbox"
              checked={form.rememberMe}
              onChange={handleChange("rememberMe")}
              disabled={loading}
            />
            Remember me on this device
          </label>

          <button type="submit" className="primary" disabled={loading}>
            {loading ? "Signing in..." : "Access dashboard"}
          </button>
        </form>
      </div>
    </div>
  );
}